import React, { useState } from "react";

// internal imports
import { forgotPassword } from "../../services/api";
import "./ForgotPassword.css";
import colors from "../../theme";

// external imports
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import SnackbarComp from "../Snackbar/SnackbarComp";
import CircularProgress from "@mui/material/CircularProgress";
import { formControlInputStyle } from "../../styles/formControlStyles";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleForgetPassword = async (event) => {
    event.preventDefault();

    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email address";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await forgotPassword(email);
      if (response.success) {
        if (response.status === "0108") {
          handleLoginWithGoogle();
        } else if (response.status === "0110") {
          navigate("/check-email", {
            state: {
              email: email,
              text: "Just click on the link in that email to reset your password.",
            },
          });
        }
      } else {
        setError(response.message);
        setStatusCode(response.status);
        setOpenSnackbar(true);
      }
    } catch (error) {
      setError(error.message || "An unknown error occurred");
      setStatusCode(error.status || 500);
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithGoogle = async () => {
    try {
      window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
    } catch (error) {
      console.error("Google login error:", error);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            component="h1"
            variant="h5"
            sx={{ marginBottom: "10px", fontWeight: "bold" }}
          >
            FORGOT PASSWORD
          </Typography>

          <Typography
            variant="body1"
            align="center"
            sx={{ marginBottom: "20px", color: "#555" }}
          >
            Enter an email address for your account, we'll send an email with a
            link to reset your password.
          </Typography>

          <Box component="form" onSubmit={handleForgetPassword} noValidate>
            <TextField
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                style: { borderRadius: 20, height: "3.5rem" },
              }}
              sx={formControlInputStyle}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                fontWeight: "bold",
                borderRadius: "20px",
                height: "3.5rem",
                marginTop: "20px",

                backgroundColor: colors.primary,
                "&:hover": {
                  backgroundColor: colors.hoverButton,
                },
              }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : "Send Email"}
            </Button>
          </Box>
        </Box>
      </Container>
      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error}
        statusCode={statusCode}
      />
    </div>
  );
}
